// Advanced tracking setup for Phase 4: Conversion Optimization
import { track } from '@/lib/analytics';

// Facebook/LinkedIn Pixel tracking
export function initFacebookPixel() {
  try {
    // Only initialize if valid pixel ID is provided
    const pixelId = process.env.FACEBOOK_PIXEL_ID;
    if (!pixelId || pixelId === 'YOUR_FACEBOOK_PIXEL_ID') {
      return;
    }
    
    const w = window as any;
    if (!w.fbq) {
      w.fbq = function(...args: any[]) {
        if (w.fbq.callMethod) {
          w.fbq.callMethod.apply(w.fbq, args);
        } else {
          w.fbq.queue.push(args);
        }
      };
      w.fbq.push = w.fbq;
      w.fbq.loaded = true;
      w.fbq.version = '2.0';
      w.fbq.queue = [];
      
      w.fbq('init', pixelId);
      w.fbq('track', 'PageView');
    }
  } catch (error) {
    console.warn('Facebook Pixel initialization failed:', error);
  }
}

export function initLinkedInPixel() {
  try {
    // Only initialize if valid partner ID is provided
    const partnerId = process.env.LINKEDIN_PARTNER_ID;
    if (!partnerId || partnerId === 'YOUR_LINKEDIN_PARTNER_ID') {
      return;
    }
    
    const w = window as any;
    if (!w._linkedin_partner_id) {
      w._linkedin_partner_id = partnerId;
      w._linkedin_data_partner_ids = w._linkedin_data_partner_ids || [];
      w._linkedin_data_partner_ids.push(w._linkedin_partner_id);
      
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
      document.getElementsByTagName('head')[0].appendChild(script);
    }
  } catch (error) {
    console.warn('LinkedIn Pixel initialization failed:', error);
  }
}

export function initHotjar() {
  try {
    // Only initialize if valid Hotjar ID is provided
    const hotjarId = process.env.HOTJAR_ID;
    if (!hotjarId || hotjarId === 'YOUR_HOTJAR_ID') {
      return;
    }
    
    const w = window as any;
    const h = w.hj = w.hj || function(...args: any[]) { (h.q = h.q || []).push(args); };
    h._hjSettings = { hjid: hotjarId, hjsv: 6 };
    
    const a = document.getElementsByTagName('head')[0];
    const r = document.createElement('script');
    r.async = true;
    r.src = 'https://static.hotjar.com/c/hotjar-' + h._hjSettings.hjid + '.js?sv=' + h._hjSettings.hjsv;
    a.appendChild(r);
  } catch (error) {
    console.warn('Hotjar initialization failed:', error);
  }
}

// Google Ads Conversion Tracking
export function trackGoogleAdsConversion(conversionLabel: string, value?: number) {
  try {
    const w = window as any;
    if (typeof w.gtag === 'function') {
      w.gtag('event', 'conversion', {
        'send_to': `AW-CONVERSION_ID/${conversionLabel}`,
        'value': value || 1.0,
        'currency': 'EUR'
      });
    }
  } catch (error) {
    console.warn('Google Ads conversion tracking failed:', error);
  }
}

// Facebook Conversion Tracking
export function trackFacebookConversion(eventName: string, params?: any) {
  try {
    const w = window as any;
    if (w.fbq) {
      w.fbq('track', eventName, params);
    }
  } catch (error) {
    console.warn('Facebook conversion tracking failed:', error);
  }
}

// LinkedIn Conversion Tracking
export function trackLinkedInConversion(conversionId: string) {
  try {
    const w = window as any;
    if (w.lintrk) {
      w.lintrk('track', { conversion_id: conversionId });
    }
  } catch (error) {
    console.warn('LinkedIn conversion tracking failed:', error);
  }
}

// Enhanced lead tracking with quality monitoring
export function trackLeadGeneration(source: string, formType: string, value?: number) {
  // Track in all platforms
  track('generate_lead', { form_type: formType, source, value });
  
  // Quality check - monitor for unusual patterns
  const qualityMetrics = {
    source,
    formType,
    timestamp: Date.now(),
    userAgent: navigator.userAgent,
    referrer: document.referrer
  };
  
  // Store for quality analysis
  const existingData = JSON.parse(localStorage.getItem('daisycon-lead-quality') || '[]');
  existingData.push(qualityMetrics);
  
  // Keep only last 100 entries
  if (existingData.length > 100) {
    existingData.splice(0, existingData.length - 100);
  }
  
  localStorage.setItem('daisycon-lead-quality', JSON.stringify(existingData));
  
  // Google Ads conversion
  trackGoogleAdsConversion('LEAD_CONVERSION_LABEL', value);
  
  // Facebook conversion
  trackFacebookConversion('Lead', {
    content_name: formType,
    content_category: 'lead_generation',
    value: value || 0,
    currency: 'EUR'
  });
  
  // LinkedIn conversion
  trackLinkedInConversion('LINKEDIN_CONVERSION_ID');
}

// Quote request tracking
export function trackQuoteRequest(service: string, estimatedValue?: number) {
  const value = estimatedValue || getEstimatedServiceValue(service);
  
  track('quote_request', { 
    service, 
    estimated_value: value,
    timestamp: Date.now()
  });
  
  trackGoogleAdsConversion('QUOTE_CONVERSION_LABEL', value);
  trackFacebookConversion('InitiateCheckout', {
    content_name: `Quote - ${service}`,
    content_category: 'quote',
    value: value,
    currency: 'EUR'
  });
}

// Phone call tracking
export function trackPhoneCall(source: string) {
  track('phone_call', { source, timestamp: Date.now() });
  trackGoogleAdsConversion('PHONE_CONVERSION_LABEL');
  trackFacebookConversion('Contact', { method: 'phone' });
}

// Helper function to estimate service values for conversion tracking
function getEstimatedServiceValue(service: string): number {
  const serviceValues: Record<string, number> = {
    'zonnepanelen': 8000,
    'warmtepomp': 12000,
    'thuisbatterij': 6000,
    'laadpaal': 1500,
    'zonneboiler': 3000,
    'elektra': 500
  };
  
  return serviceValues[service.toLowerCase()] || 5000;
}

// Traffic quality monitoring
export function monitorTrafficQuality() {
  const currentPath = window.location.pathname;
  const referrer = document.referrer;
  const userAgent = navigator.userAgent;
  
  // Check for suspicious patterns
  const qualityFlags = [];
  
  // Check for bot-like behavior
  if (userAgent.toLowerCase().includes('bot') && !userAgent.toLowerCase().includes('googlebot')) {
    qualityFlags.push('potential_bot');
  }
  
  // Check geographic location (basic)
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (!timezone.includes('Europe/Amsterdam') && !timezone.includes('Europe/Brussels')) {
    qualityFlags.push('non_target_geo');
  }
  
  // Store quality data
  if (qualityFlags.length > 0) {
    const qualityData = {
      timestamp: Date.now(),
      path: currentPath,
      referrer,
      userAgent,
      flags: qualityFlags
    };
    
    const existingData = JSON.parse(localStorage.getItem('daisycon-quality-flags') || '[]');
    existingData.push(qualityData);
    
    // Keep only last 50 entries
    if (existingData.length > 50) {
      existingData.splice(0, existingData.length - 50);
    }
    
    localStorage.setItem('daisycon-quality-flags', JSON.stringify(existingData));
  }
}

// Initialize all tracking pixels
export function initAdvancedTracking() {
  if (typeof window !== 'undefined') {
    initFacebookPixel();
    initLinkedInPixel();
    initHotjar();
    
    // Start quality monitoring
    monitorTrafficQuality();
    
    // Monitor quality on navigation
    window.addEventListener('popstate', monitorTrafficQuality);
  }
}